import React, { useContext } from 'react'
import { connect } from 'react-redux'
import MapViewBedbankPricing from '../MapViewOfferTile/MapViewBedbankPricing'
import Heading from 'components/Luxkit/Typography/Heading'
import { accountAccessShowLogin } from 'actions/UiActions'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import NumberRating from 'components/Common/NumberRating'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import AspectRatio from 'components/utils/AspectRatio'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineMultiplyIcon from 'components/Luxkit/Icons/line/LineMultiplyIcon'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'components/Luxkit/Button/TextButton'
import { useAppDispatch } from 'hooks/reduxHooks'
import LinePricetagAltIcon from 'components/Luxkit/Icons/line/LinePricetagAltIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { ImageParams } from 'components/Common/ResponsiveImage'
import { DESKTOP_TILE_WIDTH as MAP_TILE_WIDTH } from './LeMapSearchTile'

const imageParams: ImageParams = {
  mobileAspectRatio: '10:13',
  mobileWidth: '100px',
  tabletAspectRatio: '15:14',
  tabletWidth: '150px',
}

const Root = styled.div`
  position: relative;
  pointer-events: all;
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  display: flex;
  width: calc(
    100vw - 48px
  ); /* Mobile-only sizing, no need to scale with rems */
  border-radius: ${(props) => props.theme.borderRadius.S};
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
  ${mediaQueryUp.tablet} {
    max-width: ${rem(MAP_TILE_WIDTH)};
    min-height: ${rem(140)};
  }
`

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
  z-index: 1;
  border-color: ${(props) => props.theme.palette.neutral.eight};
  background-color: ${(props) => props.theme.palette.neutral.eight};
  width: ${rem(28)};
  height: ${rem(28)};
`

const StyledGroup = styled(Group)`
  padding: ${rem(12)};
`

const Container = styled.div`
  > * {
    align-self: flex-start;
  }
`

const ButtonsContainer = styled.div`
  & > * + * {
    margin-left: ${rem(8)};
  }
`

interface MappedStateProps {
  windowSearch: string;
}

interface Props {
  filters: App.OfferListFilters;
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  bestPrice?: number;
  duration: number;
  onClose?: () => void;
}

function BedbankMapSearchTile(props: Props & MappedStateProps) {
  const { filters, offer, bestPrice, duration, onClose, windowSearch } = props

  const bedbankSellPrice = offer.sell?.price
  const { name, property, images } = offer

  const dispatch = useAppDispatch()

  const { onBedbankSelectDatesPrompt } = useContext(GlobalSearchStateContext)
  const loginHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    dispatch(accountAccessShowLogin())
  }

  const onSelectDatesPrompt: React.MouseEventHandler<HTMLAnchorElement> = (
    e,
  ) => {
    e.preventDefault()
    onBedbankSelectDatesPrompt?.(offer.id, offer.type, windowSearch)
  }

  const price = bestPrice ?? bedbankSellPrice

  return (
    <Root>
      <Group direction="horizontal" fullWidth>
        <AspectRatio tabletWidth="150px" width="100px">
          {onClose && (
            <StyledIconButton
              size="small"
              shape="circle"
              kind="secondary"
              onClick={(e) => {
                e.preventDefault()
                onClose()
              }}
            >
              <LineMultiplyIcon />
            </StyledIconButton>
          )}
          <ImageCarousel
            images={images}
            imageParams={imageParams}
            eagerLoadFirstImage
          />
        </AspectRatio>
        <StyledGroup
          direction="vertical"
          fullWidth
          verticalAlign="space-between"
          gap={8}
        >
          <Group direction="vertical" fullWidth gap={4}>
            {!!property.rating && <Group direction="horizontal" fullWidth horizontalAlign="space-between">
              <NumberRating
                  variant="xs"
                  rating={property.rating}
                  total={property.reviewsTotal}
                  onlyNumber
                />
              <BookmarkButton offer={offer} />
            </Group>}
            <div>
              <Group direction="horizontal" fullWidth horizontalAlign="space-between">
                <Heading wrap="pre-wrap" variant="heading6">{name}</Heading>
                {!property.rating && <BookmarkButton offer={offer} />}
              </Group>
              <ProductTypeLabel productType={offer.productType} kind="plain" />
            </div>
          </Group>

          <WalledContent
            enforced={offer.walledGarden}
            wall={
              <>
                <CaptionTextBlock
                  variant="medium"
                  weight="bold"
                  startIcon={<LinePricetagAltIcon />}
                >
                  Members get exclusive rates
                </CaptionTextBlock>
                <ButtonsContainer>
                  <TextButton
                    kind="secondary"
                    onClick={loginHandler}
                    size="small"
                  >
                    Log In
                  </TextButton>
                  <TextButton
                    kind="primary"
                    onClick={loginHandler}
                    size="small"
                  >
                    Sign up for free
                  </TextButton>
                </ButtonsContainer>
              </>
            }
          >
            <Container>
              {!price && (
                <TextLink size="caption" onClick={onSelectDatesPrompt}>
                  Select dates to view rates
                </TextLink>
              )}
              {price && (
                <MapViewBedbankPricing
                  filters={filters ?? {}}
                  offer={offer}
                  price={price}
                  duration={duration}
                  alignStart
                />
              )}
            </Container>
          </WalledContent>
        </StyledGroup>
      </Group>
    </Root>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (state): MappedStateProps => {
    return {
      windowSearch: state.router.location.search,
    }
  },
)(BedbankMapSearchTile)
